// KOA-5857 — Editable-filename popover for binary / image cell downloads.
//
// The cell editors used to fire `downloadBase64` directly on the `↓`
// click, with a filename derived from `<columnName>.<detected-ext>`.
// Two problems:
//
//   1. The detected extension is best-effort; if the magic-byte sniff
//      misses, the user gets `<columnName>.bin` for a file they know
//      is a .zip / .docx / whatever.
//   2. The column name often isn't what the user wants the file
//      called (e.g. a column named `Document` saving an invoice they'd
//      rather call `invoice-2026-05.pdf`).
//
// This popover sits between the click and the download. It opens
// anchored to the trigger button, pre-fills with the best-guess
// filename, lets the user edit, and only fires the download on Save.
// Cancel / Esc / backdrop-click dismiss without saving.

import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

export interface IDownloadFilenamePopoverProps {
  // Anchor element — popover positions itself near the trigger
  // button's bounding rect.
  anchor: HTMLElement;
  // Pre-fill value for the filename input.
  defaultFilename: string;
  onConfirm: (filename: string) => void;
  onClose: () => void;
}

const DownloadFilenamePopover: React.FC<IDownloadFilenamePopoverProps> = (
  props: IDownloadFilenamePopoverProps
): JSX.Element | null => {
  const { anchor, defaultFilename, onConfirm, onClose } = props;
  const [filename, setFilename] = useState<string>(defaultFilename);
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Compute viewport-anchored position from the trigger's bounding
  // rect. Prefer below the anchor; flip above when there isn't room.
  // Horizontally clamp so the popover never leaves the viewport.
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);
  useEffect((): void => {
    const rect: DOMRect = anchor.getBoundingClientRect();
    const POPOVER_W: number = 320;
    const POPOVER_H_APPROX: number = 110;
    const GAP: number = 6;
    const PAD: number = 12;
    const vw: number = window.innerWidth;
    const vh: number = window.innerHeight;
    const spaceBelow: number = vh - rect.bottom;
    const showAbove: boolean = spaceBelow < POPOVER_H_APPROX + GAP + PAD;
    const top: number = showAbove ? rect.top - GAP - POPOVER_H_APPROX : rect.bottom + GAP;
    let left: number = rect.left;
    if (left + POPOVER_W > vw - PAD) {
      left = Math.max(PAD, vw - PAD - POPOVER_W);
    }
    setPosition({ top, left });
  }, [anchor]);

  // Initial focus on the input, with the filename's STEM selected
  // (everything before the last "."). Users almost always rename the
  // stem and keep the extension, so pre-selecting the stem makes the
  // common case a single keystroke.
  useEffect((): void => {
    const node: HTMLInputElement | null = inputRef.current;
    if (node === null) {
      return;
    }
    node.focus();
    const dotIdx: number = defaultFilename.lastIndexOf(".");
    if (dotIdx > 0) {
      node.setSelectionRange(0, dotIdx);
    } else {
      node.select();
    }
  }, [defaultFilename]);

  // Dismiss on Esc, scroll, or click outside the popover.
  useEffect((): (() => void) => {
    const handleKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
      }
    };
    const handleClickOutside = (e: MouseEvent): void => {
      const target: Node | null = e.target instanceof Node ? e.target : null;
      if (target === null) {
        return;
      }
      // Anchor click should not dismiss — the anchor's own handler
      // toggles the popover open / closed.
      if (anchor.contains(target)) {
        return;
      }
      if (popoverRef.current !== null && popoverRef.current.contains(target)) {
        return;
      }
      onClose();
    };
    const handleScroll = (): void => onClose();
    document.addEventListener("keydown", handleKey, true);
    document.addEventListener("mousedown", handleClickOutside, true);
    window.addEventListener("scroll", handleScroll, true);
    return (): void => {
      document.removeEventListener("keydown", handleKey, true);
      document.removeEventListener("mousedown", handleClickOutside, true);
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, [anchor, onClose]);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    // The popover is portaled, but React still bubbles its submit through the component tree —
    // and inside the table editor's Data tab that tree has the columns editor's antd <Form> above
    // it, which would take this submit as its own.
    e.stopPropagation();
    const trimmed: string = filename.trim();
    if (trimmed.length === 0) {
      return;
    }
    onConfirm(trimmed);
  };

  if (position === null) {
    return null;
  }

  return createPortal(
    <div
      ref={popoverRef}
      className="kuika-download-popover"
      role="dialog"
      aria-label="Download as"
      style={{ top: position.top, left: position.left }}
      onClick={(e: React.MouseEvent<HTMLDivElement>): void => e.stopPropagation()}
    >
      <form onSubmit={handleSubmit}>
        <label className="kuika-download-popover__label" htmlFor="kuika-download-popover-input">
          Save as
        </label>
        <input
          ref={inputRef}
          id="kuika-download-popover-input"
          className="kuika-download-popover__input"
          type="text"
          value={filename}
          spellCheck={false}
          autoComplete="off"
          onChange={(e: React.ChangeEvent<HTMLInputElement>): void => setFilename(e.target.value)}
        />
        <div className="kuika-download-popover__actions">
          <button type="button" className="kuika-download-popover__cancel" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="kuika-download-popover__save" disabled={filename.trim().length === 0}>
            Download
          </button>
        </div>
      </form>
    </div>,
    document.body
  );
};

export default DownloadFilenamePopover;
